/**
 * Native screen-orientation lock, best effort (design `docs/design/mobile-layout.md` §5).
 *
 * The honest browser limit: `screen.orientation.lock()` is honoured only while the page is
 * FULLSCREEN, and in practice only by Chromium on Android (and installed PWAs). iOS Safari
 * has no `lock()` and no element fullscreen on iPhone; desktop browsers reject it. So the
 * Orientation setting ALWAYS holds the in-game layout (layoutMode.ts), and this module
 * additionally asks the browser to stop rotating the screen where it can — failing silently
 * everywhere else. Fullscreen needs a user gesture, so it is only ever requested from one
 * (the settings change itself, or the first tap after a reload with a lock saved), and only
 * on a touch-first device: a desktop never rotates, so a desktop is never sent fullscreen.
 *
 * Ports are injected so the decision table is tested under `node --test`.
 */

import type { OrientationPref } from './layoutMode.js';

export interface OrientationLockPorts {
  /** `screen.orientation`, when the browser has it. */
  orientation?: {
    lock?: (o: 'portrait' | 'landscape') => Promise<void>;
    unlock?: () => void;
  } | null;
  /** `document.fullscreenElement !== null`. */
  isFullscreen: () => boolean;
  /** `document.documentElement.requestFullscreen`, when present. */
  requestFullscreen?: (() => Promise<void>) | null;
  /** `(pointer: coarse)`. */
  coarsePointer: boolean;
}

export type LockOutcome = 'unlocked' | 'locked' | 'unsupported' | 'refused' | 'skipped-desktop';

/**
 * Apply the setting to the native lock. `fromGesture` says the call is inside a user
 * gesture (so a fullscreen request is allowed). Never throws, never rejects.
 */
export async function applyNativeOrientationLock(
  pref: OrientationPref,
  ports: OrientationLockPorts,
  fromGesture: boolean,
): Promise<LockOutcome> {
  const o = ports.orientation;
  if (pref === 'auto') {
    try {
      o?.unlock?.();
    } catch {
      // unlock throws where lock was never supported; nothing to undo.
    }
    return 'unlocked';
  }
  if (!ports.coarsePointer) return 'skipped-desktop';
  if (!o || typeof o.lock !== 'function') return 'unsupported';
  try {
    if (!ports.isFullscreen()) {
      if (!fromGesture || typeof ports.requestFullscreen !== 'function') return 'unsupported';
      await ports.requestFullscreen();
    }
    await o.lock(pref);
    return 'locked';
  } catch {
    // NotSupportedError / SecurityError / a refused fullscreen: the layout lock stands alone.
    return 'refused';
  }
}

/** The live browser ports (guarded: any missing API reads as absent). */
export function browserLockPorts(): OrientationLockPorts {
  const hasWindow = typeof window !== 'undefined' && typeof document !== 'undefined';
  if (!hasWindow) {
    return {
      orientation: null,
      isFullscreen: () => false,
      requestFullscreen: null,
      coarsePointer: false,
    };
  }
  const so = (screen as unknown as { orientation?: OrientationLockPorts['orientation'] })
    .orientation;
  const root = document.documentElement as HTMLElement & {
    requestFullscreen?: (opts?: { navigationUI?: string }) => Promise<void>;
  };
  return {
    orientation: so
      ? {
          lock: typeof so.lock === 'function' ? (v) => so.lock!.call(so, v) : undefined,
          unlock: typeof so.unlock === 'function' ? () => so.unlock!.call(so) : undefined,
        }
      : null,
    isFullscreen: () => document.fullscreenElement != null,
    requestFullscreen:
      typeof root.requestFullscreen === 'function'
        ? () => root.requestFullscreen!({ navigationUI: 'hide' })
        : null,
    coarsePointer:
      typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches,
  };
}
